import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  input,
  signal,
  viewChildren
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

const NON_ALPHANUMERIC = /[^a-zA-Z0-9]/g;

@Component({
  selector: 'app-code-input',
  templateUrl: './code-input.component.html',
  styleUrls: ['./code-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => CodeInputComponent)
    }
  ]
})
export class CodeInputComponent implements ControlValueAccessor {
  readonly length = input<number>(6);
  readonly invalid = input<boolean>(false);
  readonly label = input<string>('');

  // viewChildren no admite campos privados con #
  private readonly cells = viewChildren<ElementRef<HTMLInputElement>>('cell');
  readonly #chars = signal<string[]>([]);

  readonly #isDisabled = signal<boolean>(false);
  protected readonly isDisabled = this.#isDisabled.asReadonly();
  readonly cellValues = computed<string[]>(() =>
    Array.from({ length: this.length() }, (_, index) => this.#chars()[index] ?? '')
  );

  #onChange: (value: string) => void = () => {};
  #onTouched: () => void = () => {};

  writeValue(value: string | null): void {
    this.#chars.set([...this.#sanitize(value ?? '')].slice(0, this.length()));
    this.#syncCells();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.#onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.#onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.#isDisabled.set(isDisabled);
  }

  protected onInput(index: number, event: Event): void {
    const cell = event.target as HTMLInputElement;
    const text = this.#sanitize(cell.value);

    if (!text) {
      this.#setChar(index, '');
      return;
    }

    this.#fill(index, text);
  }

  protected onPaste(index: number, event: ClipboardEvent): void {
    event.preventDefault();
    const text = this.#sanitize(event.clipboardData?.getData('text') ?? '');

    if (!text) {
      return;
    }

    this.#fill(text.length >= this.length() ? 0 : index, text);
  }

  protected onKeydown(index: number, event: KeyboardEvent): void {
    switch (event.key) {
      case 'Backspace':
        event.preventDefault();
        if (this.cellValues()[index]) {
          this.#setChar(index, '');
        } else if (index > 0) {
          this.#setChar(index - 1, '');
          this.#focus(index - 1);
        }
        break;
      case 'Delete':
        event.preventDefault();
        this.#setChar(index, '');
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.#focus(index - 1);
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.#focus(index + 1);
        break;
    }
  }

  protected onFocus(event: FocusEvent): void {
    (event.target as HTMLInputElement).select();
  }

  protected onFocusOut(event: FocusEvent, group: HTMLElement): void {
    if (!group.contains(event.relatedTarget as Node | null)) {
      this.#onTouched();
    }
  }

  #fill(start: number, text: string): void {
    const chars = this.cellValues().slice();
    const accepted = [...text].slice(0, this.length() - start);

    accepted.forEach((char, offset) => (chars[start + offset] = char));
    this.#update(chars);
    this.#focus(Math.min(start + accepted.length, this.length() - 1));
  }

  #setChar(index: number, char: string): void {
    const chars = this.cellValues().slice();
    chars[index] = char;
    this.#update(chars);
  }

  #update(chars: string[]): void {
    this.#chars.set(chars);
    this.#syncCells();
    this.#onChange(chars.join(''));
  }

  #syncCells(): void {
    const values = this.cellValues();
    this.cells().forEach((cell, index) => (cell.nativeElement.value = values[index]));
  }

  #focus(index: number): void {
    this.cells()[index]?.nativeElement.focus();
  }

  #sanitize(value: string): string {
    return value.replace(NON_ALPHANUMERIC, '');
  }
}
